"use client"

import { cn } from "../../lib/utils"
import { type VariantProps, cva } from "class-variance-authority"
import * as React from "react"

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full min-w-0 overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  )
}

const tableRowVariants = cva(
  "data-[state=selected]:bg-muted border-b transition-colors",
  {
    variants: {
      // A header or a layout row is not hoverable: it points at nothing.
      tone: {
        default: "hover:bg-muted/50",
        static: "hover:bg-transparent",
        accent: "bg-accent/40 hover:bg-muted/40",
      },
      flush: { true: "border-0", false: "" },
    },
    defaultVariants: { tone: "default", flush: false },
  },
)

function TableRow({
  className,
  tone,
  flush,
  ...props
}: React.ComponentProps<"tr"> & VariantProps<typeof tableRowVariants>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(tableRowVariants({ tone, flush }), className)}
      {...props}
    />
  )
}

function TableHead({
  className,
  muted = false,
  ...props
}: React.ComponentProps<"th"> & { muted?: boolean }) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        muted ? "text-muted-foreground" : "text-foreground",
        className,
      )}
      {...props}
    />
  )
}

const tableCellVariants = cva(
  "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      // Secondary data reads quieter than the row's subject.
      tone: {
        default: "",
        muted: "text-muted-foreground",
        faint: "text-muted-foreground/50",
        strong: "text-foreground",
        success: "text-success",
        destructive: "text-destructive",
      },
      surface: {
        default: "",
        muted: "bg-muted/30",
        raised: "bg-muted/40",
      },
    },
    defaultVariants: { tone: "default", surface: "default" },
  },
)

function TableCell({
  className,
  tone,
  surface,
  ...props
}: React.ComponentProps<"td"> & VariantProps<typeof tableCellVariants>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(tableCellVariants({ tone, surface }), className)}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
